import type { CenterProps } from '@hover/blueprint';
import { Center, Flex, motion, transition } from '@hover/blueprint';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import React from 'react';

import { useEffectOnMount } from 'src/shared/hooks';
import { fadePresence } from 'src/shared/utils/motion';

import { useHomeownerTranslate } from '../../hooks';
import type {
  LinkImpression,
  LinkPressed,
} from '../../hooks/useImperativeTracking';
import { useImperativeTracking } from '../../hooks/useImperativeTracking';
import { AnimatedEllipsis } from '../AnimatedEllipsis';

export interface SwatchCalloutProps extends CenterProps {
  colorScheme?: 'light' | 'dark';
  isLoading?: boolean;
  trackingData: {
    linkImpression: LinkImpression;
    linkPressed: LinkPressed;
  };
  darkBackgroundColor?: string;
  lightBackgroundColor?: string;
  manufacturerUrl?: string;
}

const MotionAnimatedEllipsis = motion(AnimatedEllipsis);
const MotionFlex = motion(Flex);

/**
 * SwatchCallout is a generic implementation (agnostic of data source). Do not
 * add props that require usage of a specific data source. Create or
 * use a wrapper that destructures the data source into the props needed for this
 * component instead.
 *
 * @see ./VariationEditSwatchCallout.tsx for a wrapper example
 */
export const SwatchCallout: React.FC<SwatchCalloutProps> = ({
  isLoading = false,
  colorScheme = 'light',
  darkBackgroundColor = 'neutral.900',
  lightBackgroundColor = 'neutral.0',
  manufacturerUrl,
  trackingData,
  children,
  ...props
}) => {
  const t = useHomeownerTranslate();

  const isDark = colorScheme === 'dark';
  const background = isDark ? darkBackgroundColor : lightBackgroundColor;
  const { trackingRef } = useImperativeTracking();

  useEffectOnMount(() => {
    if (manufacturerUrl) {
      trackingRef.current?.linkImpression(trackingData.linkImpression);
    }
  });

  const handleOnClick = () => {
    trackingRef.current?.linkPressed(trackingData.linkPressed);
  };

  return (
    <Center
      as="a"
      bg={isLoading ? 'neutral.50' : background}
      borderRadius={isDark ? 400 : 0}
      color={isDark ? 'neutral.0' : 'neutral.900'}
      data-group
      display="flex"
      height={500}
      href={manufacturerUrl}
      overflow="hidden"
      padding={isDark ? 400 : 0}
      pointerEvents={isLoading ? 'none' : 'auto'}
      position="relative"
      target="_blank"
      textDecoration="none"
      {...transition.easing250('background')}
      {...props}
      onClick={manufacturerUrl ? handleOnClick : undefined}
    >
      <MotionConfig transition={{ duration: 0.25 }}>
        <AnimatePresence initial={false}>
          {isLoading ? (
            <MotionAnimatedEllipsis
              color="neutral.900"
              height="25"
              label={t('materialCallouts.calloutButtonLoading')}
              position="absolute"
              {...fadePresence}
            />
          ) : (
            <MotionFlex
              alignItems="center"
              direction="row"
              height="100%"
              key="swatch"
              width="100%"
              {...fadePresence}
            >
              {children}
            </MotionFlex>
          )}
        </AnimatePresence>
      </MotionConfig>
    </Center>
  );
};
